import { useState, useEffect, useRef } from 'react';
import { createRootRoute, Outlet, useNavigate, useLocation } from '@tanstack/react-router';
import { useAuth } from '../hooks/useAuth.js';
import { useTheme } from '../hooks/useTheme.js';
import { Header } from '../components/layout/Header/index.js';
import { Sidebar } from '../components/layout/Sidebar/index.js';
import { LoadingScreen } from '../components/feedback/LoadingScreen/index.js';

/**
 * @graph-node {Page}
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business ルートレイアウト。認証済み社員全員に Header・Sidebar・Outlet の共通レイアウトを提供（pipeline 操作の認可は API 側で pipeline_permissions により判定）
 * @graph-connects useAuth [calls] 認証フック呼び出し
 * @graph-connects Header [calls] ヘッダーコンポーネント呼び出し
 * @graph-connects Sidebar [calls] サイドバーコンポーネント呼び出し
 * @graph-connects LoadingScreen [calls] ローディング画面コンポーネント呼び出し
 */
export const Route = createRootRoute({
  component: RootLayout,
});

/** @graph-connects none */
function RootLayout() {
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // テーマ初期化（data-theme属性をhtmlに適用）
  useTheme();

  const isLoginPage = location.pathname === '/login';

  // location.pathname / navigatedRef ガード: 認証 redirect useEffect 内で navigate を
  // 連続発火させない。location.pathname を deps に含めると route transition 中に
  // 多重 render で effect が再発火し、auth re-fetch 等で isLoading / isAuthenticated
  // が変動するシナリオも合わさって `history.notify` → `router.load` →
  // `setIsTransitioning` の同期連発で Minified React error #185 を起こす
  // （docs/web/product-innovation-lab.md「1 回ガード ref」規則）。
  // __root は SPA 起動中ずっと mount されるため、auth 回復時に navigatedRef を
  // リセットして次回 auth 失敗時にも redirect が走るようにする。
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  const navigatedRef = useRef(false);

  // 認証リダイレクト
  // ベースゲートは「認証済み社員」のみ。各機能（pipeline trigger 等）の認可は
  // API 側で pipeline_permissions により判定する（admin / creator / targetDbAccess）。
  useEffect(() => {
    if (isAuthenticated) {
      navigatedRef.current = false;
      return;
    }
    if (navigatedRef.current) return;
    if (isLoginPage || isLoading) return;
    navigatedRef.current = true;
    void navigate({ to: '/login', search: { redirect: pathnameRef.current } });
  }, [isAuthenticated, isLoading, isLoginPage, navigate]);

  // ログインページはレイアウトなし
  if (isLoginPage) {
    return <Outlet />;
  }

  // ローディング中
  if (isLoading) {
    return <LoadingScreen />;
  }

  // 未認証（リダイレクト中）
  if (!isAuthenticated) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg-base)]">
      {/* 背景グラデーションオーブ */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="gradient-orb gradient-orb-primary w-[500px] h-[500px] -top-64 -right-64 opacity-10" />
        <div className="gradient-orb gradient-orb-success w-[400px] h-[400px] bottom-0 -left-48 opacity-5" />
      </div>

      <Header
        user={user}
        onLogout={logout}
        onMenuToggle={() => {
          setIsSidebarOpen(true);
        }}
      />
      <div className="relative flex min-h-[calc(100vh-3.5rem)]">
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => {
            setIsSidebarOpen(false);
          }}
        />
        <main className="flex-1 overflow-y-auto">
          <div className="p-4 md:p-6 lg:p-8 animate-content-reveal">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
